import {Swiper, SwiperSlide} from 'swiper/react';
import { Navigation, Pagination, Scrollbar, A11y, EffectCube } from 'swiper';
import 'swiper/css';
import { useCallback, useEffect, useRef, useState } from 'react';
import {  useTranslation } from 'next-i18next';
import { CarouselNavigator, CarouselViewport, Dot, PostBlocWrapper, PostCardStyled } from './styles';
import { ArrowLeft, ArrowRight } from './arrowIcons';
import { useRouter } from 'next/router';
import { StaticI18nLink } from '../../Link';

const getWindowWidth = () => {
  return
}

/* content stops scaling past the 1216px grid the other v2 sections
   use (InnerV2); on ultra-wide monitors the extra space becomes margin */
const CONTENT_MAX_WIDTH = 1216;
const SIZING_MAX_WIDTH = 1360;
const clampSizingWidth = (width) => Math.min(width || 0, SIZING_MAX_WIDTH);

export const BlogPostsCarousel = ({ posts }) => {
  const [activeSlide, setActiveSlide] = useState(0); // manter o state para atualizar os bullets
  const sliderRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(getWindowWidth());
  const increaseSlidesScreenBreakpoint = 576;
  const lastSlideActiveDesktop = 2;

  useEffect(() => {
    function handleResize() {
      /* clientWidth (scrollbar excluded) keeps the JS margins in sync
         with the CSS %-based margins of the head and navigator */
      setWindowWidth(document.documentElement.clientWidth);
    }

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getActiveSwiperSlide = () => sliderRef?.current?.swiper?.activeIndex;

  const handlePrev = useCallback(() => {
    if (!sliderRef.current || isFirstSlideActive()) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current || isLastSlideActive()) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  const postsToSlide = () => {
    let chunkSize = 3;

    if (windowWidth <= 576) {
      chunkSize = 1;
    }

    const postsChunks = [];

    for (let i = 0; i < posts.length; i += chunkSize) {
      postsChunks.push(posts.slice(i, i + chunkSize));
    }

    return postsChunks;
  };

  const isFirstSlideActive = () => getActiveSwiperSlide() === 0;
  
  const isLastSlideActive = () => {
    let isLastSlideActive = getActiveSwiperSlide() === lastSlideActiveDesktop;
    if (shoudIncreaseSlides()) {
      isLastSlideActive = getActiveSwiperSlide() === (posts?.length - 1)
    }

    return isLastSlideActive;
  }

  const shoudIncreaseSlides = () => windowWidth <= increaseSlidesScreenBreakpoint;

  /* centres the content on the 1216px grid: 5.555vw until the leftover
     viewport exceeds it, then the extra space becomes margin. the
     CarouselViewport clips at this box so wide screens don't reveal the
     neighbouring slides in the side margins */
  const carouselSideMargin =
    windowWidth < 992
      ? `6.15vw`
      : `${Math.max(0.0555 * windowWidth, (windowWidth - CONTENT_MAX_WIDTH) / 2)}px`;

  const carouselNavigator = () => {
    return (
      <CarouselNavigator>
        <div
          className={`prev_slide ${isFirstSlideActive() ? 'disabled' : '' }`}
          onClick={handlePrev}
        >
          <ArrowLeft className={'arrow'}/>
        </div>
        <div className='dotsNavigationWrapper'>
          <Dot active={getActiveSwiperSlide() === 0}/>
          <Dot active={getActiveSwiperSlide() === 1}/>
          <Dot active={getActiveSwiperSlide() === 2}/>
          {
            shoudIncreaseSlides() && (
              <>
                <Dot active={getActiveSwiperSlide() === 3}/>
                <Dot active={getActiveSwiperSlide() === 4}/>
                <Dot active={getActiveSwiperSlide() === 5}/>
                <Dot active={getActiveSwiperSlide() === 6}/>
                <Dot active={getActiveSwiperSlide() === 7}/>
                <Dot active={getActiveSwiperSlide() === 8}/>
              </>
            )
          }
        </div>
        <div
          className={`next_slide ${isLastSlideActive() ? 'disabled' : ''}`}
          onClick={handleNext}
        >
          <ArrowRight className={'arrow'}/>
        </div>
      </CarouselNavigator>
    )
  }

  return (
    <>
      <CarouselViewport style={{ marginLeft: carouselSideMargin, marginRight: carouselSideMargin }}>
      <Swiper
        ref={sliderRef}
        style={{
          overflow: 'visible',
        }}
        loop={false}
        noSwipingSelector='.postLink'
        modules={[Pagination]}
        spaceBetween={0.0694 * clampSizingWidth(windowWidth)}
        slidesPerView={1}
        scrollbar={{ draggable: true }}
        onSlidePrevTransitionStart={({ activeIndex }) => setActiveSlide(activeIndex)}
        onSlideNextTransitionStart={({ activeIndex }) => setActiveSlide(activeIndex)}
        >
        {postsToSlide().map((chunk, index) => (
          <SwiperSlide key={index}>
              <PostBlocWrapper>
                {chunk.map((post) => (
                  <PostCard
                    key={post.metadata.imagem_inicio.url}
                    title={post.title}
                    image={`${post.metadata.imagem_inicio.imgix_url}?w=500&auto=format,compress&q=80`}
                    slug={post.slug}
                    author={post.metadata.autor}
                    outlet={post.metadata.veiculo}
                  />
                  ))}
              </PostBlocWrapper>
            </SwiperSlide>
          ))}
      </Swiper>
      </CarouselViewport>
      {carouselNavigator()}
    </>
  )
}

const PostCard = ({ title, image, slug, author, outlet }) => {
  const { t } = useTranslation();
  const [windowWidth, setWindowWidth] = useState(getWindowWidth());
  const router = useRouter();

  useEffect(() => {
    function handleResize() {
      setWindowWidth(document.documentElement.clientWidth);
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const outletLogo =
    outlet?.metadata?.logo?.imgix_url || outlet?.metadata?.logo?.url;
  const authorPhoto =
    author?.metadata?.foto?.imgix_url || author?.metadata?.foto?.url;
  const authorInitials = (author?.title || '')
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <PostCardStyled windowWidth={clampSizingWidth(windowWidth)} image={image} >
      {/* "publicado em" badge: outlet logo pinned to the top of the card */}
      {outlet && (
        <div className={'postOutletBadge'} title={outlet.title}>
          {outletLogo && (
            <img src={outletLogo} alt={outlet.title} loading="lazy" />
          )}
          <span className={outletLogo ? 'sr-only-name' : undefined}>
            {outlet.title}
          </span>
        </div>
      )}
      <div className={'postOverlay'}>
        {/* editorial byline: who signs the piece */}
        {author && (
          <div className={'postByline'}>
            {authorPhoto ? (
              <img
                className={'postAvatar'}
                src={authorPhoto}
                alt={author.title}
                loading="lazy"
              />
            ) : (
              <span className={'postAvatar postAvatarInitials'} aria-hidden="true">
                {authorInitials}
              </span>
            )}
            <span className={'postAuthorText'}>
              <b>{author.title}</b>
              {author.metadata?.cargo && <i>{author.metadata.cargo}</i>}
            </span>
          </div>
        )}
        <div className={'postTextWrapper'}>
          <p className={'postTitle'}>{title}</p>
        </div>
        <div className={'postLinkWrapper'}>
          <StaticI18nLink href={`/blog/${slug}`}>
            <a className={'postLink'}>{t('blogSection.readMore')}</a>
          </StaticI18nLink>
          <img className={'arrowLeft'} src="/landings/assets/icons/forward.svg" alt={t('blogSection.arrowForward')} />
        </div>
      </div>
    </PostCardStyled>
  );
}
